import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, Package, CheckCircle2, Truck, Clock, XCircle } from "lucide-react";
import Seo from "@/components/Seo";
import api, { formatApiError } from "@/lib/api";

const STEPS = [
  { key: "pending", label: "Order Placed", icon: Clock },
  { key: "paid", label: "Payment Confirmed", icon: CheckCircle2 },
  { key: "processing", label: "Preparing", icon: Package },
  { key: "shipped", label: "Shipped", icon: Truck },
  { key: "delivered", label: "Delivered", icon: CheckCircle2 },
];

const ORDER = ["pending", "paid", "processing", "shipped", "delivered"];

export default function Track() {
  const { invoice: invoiceParam } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(invoiceParam || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const lookup = useCallback(async (inv) => {
    const code = (inv || "").trim();
    if (!code) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await api.get(`/track/${encodeURIComponent(code)}`);
      setOrder(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Order not found.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (invoiceParam) lookup(invoiceParam);
  }, [invoiceParam, lookup]);

  const submit = (e) => {
    e.preventDefault();
    const code = query.trim();
    if (code) navigate(`/track/${encodeURIComponent(code)}`);
  };

  const cancelled = order?.status === "cancelled";
  const currentIdx = order ? ORDER.indexOf(order.status) : -1;

  return (
    <div className="min-h-screen bg-[#050505] text-slate-200 pt-28 pb-24" data-testid="track-page">
      <Seo title="Track Your Order — Dark Growers Genetics" description="Check the status of your Dark Growers Genetics order." noindex />
      <div className="max-w-2xl mx-auto px-5">
        <p className="label-caps text-emerald-400 mb-2">Order Tracking</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white mb-8">Track Your Order</h1>

        <form onSubmit={submit} className="flex gap-2 mb-10" data-testid="track-form">
          <input
            data-testid="track-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Enter your order number (e.g. DG260711…)"
            className="flex-1 bg-[#0B0B0B] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-4 py-3 transition-colors placeholder:text-slate-600"
          />
          <button
            type="submit"
            data-testid="track-submit"
            className="inline-flex items-center gap-2 bg-emerald-500 text-[#050505] hover:bg-emerald-400 transition-colors font-semibold px-5 py-3"
          >
            <Search size={17} /> Track
          </button>
        </form>

        {loading && <p className="text-slate-500 text-sm" data-testid="track-loading">Looking up your order…</p>}

        {error && !loading && (
          <div className="flex items-center gap-3 border border-red-500/30 bg-red-500/5 px-4 py-4 text-sm text-red-300" data-testid="track-error">
            <XCircle size={18} /> {error}
          </div>
        )}

        {order && !loading && (
          <div className="border border-white/10 bg-[#0B0B0B] p-6 sm:p-8" data-testid="track-result">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-8">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Order</p>
                <p className="font-display text-2xl text-white" data-testid="track-invoice">{order.invoice}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Total</p>
                <p className="text-emerald-400 text-lg font-semibold">${Number(order.total).toFixed(2)}</p>
              </div>
            </div>

            {cancelled ? (
              <div className="flex items-center gap-3 border border-red-500/30 bg-red-500/5 px-4 py-4 text-sm text-red-300 mb-8" data-testid="track-cancelled">
                <XCircle size={18} /> This order was cancelled. Contact support if you have questions.
              </div>
            ) : (
              <div className="space-y-0 mb-8" data-testid="track-timeline">
                {STEPS.map((step, i) => {
                  const done = i <= currentIdx;
                  const active = i === currentIdx;
                  const Icon = step.icon;
                  return (
                    <div key={step.key} className="flex items-start gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${done ? "bg-emerald-500 border-emerald-500 text-[#050505]" : "border-white/15 text-slate-600"}`}>
                          <Icon size={16} />
                        </div>
                        {i < STEPS.length - 1 && (
                          <div className={`w-0.5 h-8 ${i < currentIdx ? "bg-emerald-500" : "bg-white/10"}`} />
                        )}
                      </div>
                      <div className="pt-1.5">
                        <p className={`text-sm ${active ? "text-emerald-400 font-semibold" : done ? "text-slate-200" : "text-slate-600"}`}>
                          {step.label}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="border-t border-white/10 pt-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Items</p>
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm py-1.5 border-b border-white/5">
                  <span className="text-slate-300">{it.name}</span>
                  <span className="text-slate-500">×{it.quantity}</span>
                </div>
              ))}
              {order.ship_to && <p className="text-xs text-slate-500 mt-4">Shipping to {order.ship_to}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
